<template>
    <div>
        <div class="floating-cart" @click="cartState.open = true">
            <span class="cart-icon">🛒</span>
            <span class="cart-badge" v-if="totalItems > 0">{{ totalItems }}</span>
        </div>

        <div class="cart-overlay" v-if="cartState.open" @click="cartState.open = false"></div>

        <div class="cart-drawer" :class="{ 'open': cartState.open }">
            <div class="cart-header">
                <div class="header-left">
                    <button class="back-btn" v-if="cartState.step === 2" @click="cartState.step = 1">←</button>
                    <h2>{{ cartState.step === 1 ? $t('product.cart.titleList') : $t('product.cart.titleForm') }}</h2>
                </div>
                <button class="close-btn" @click="cartState.open = false">×</button>
            </div>

            <div class="cart-body">
                <div v-if="cartState.step === 1">
                    <div v-if="cartState.list.length === 0" class="empty-cart">
                        <p>{{ $t('product.cart.empty') }}</p>
                    </div>
                    <div v-else class="cart-list">
                        <div class="cart-item" v-for="(item, index) in cartState.list" :key="item.id">
                            <div class="item-img">
                                <img :src="iconOf(item)" alt="" />
                            </div>
                            <div class="item-info">
                                <div class="item-head">
                                    <h4>{{ item.title || item.name }}</h4>
                                    <button class="remove-btn" type="button" :aria-label="$t('product.cart.delete')"
                                        :title="$t('product.cart.delete')" @click="removeFromCart(index)">
                                        <svg viewBox="0 0 24 24" aria-hidden="true">
                                            <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v8h-2V9zm4 0h2v8h-2V9zM7 9h2v8H7V9z"
                                                fill="currentColor" />
                                        </svg>
                                    </button>
                                </div>
                                <p class="item-meta" v-if="item.typeName">{{ $t(`product.nav.` + item.typeName) }}<span
                                        v-if="item.subTypeName"> · {{ $t(`product.sub.` + item.subTypeName) }}</span></p>
                                <div class="item-foot">
                                    <span class="item-price">{{ locale === 'zh' ? '￥' + (item.price * (item.rate || 6.8)).toFixed(2) : 'US$' +
                                        Number(item.price || 0).toFixed(2) }}</span>
                                    <div class="qty-control">
                                        <button type="button" @click="decreaseQty(index)">−</button>
                                        <input type="number" v-model.number="item.quantity" min="1" readonly />
                                        <button type="button" @click="increaseQty(index)">+</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="cartState.step === 2" class="checkout-form">
                    <div class="honeypot" aria-hidden="true">
                        <input type="text" name="company_website" v-model="honeypot" tabindex="-1" autocomplete="off" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.name') }} <span>*</span></label>
                        <input type="text" v-model="formData.name" :placeholder="$t('product.form.namePlaceholder')"
                            maxlength="50" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.location') }} <span>*</span></label>
                        <input type="text" v-model="formData.area" :placeholder="$t('product.form.locationPlaceholder')"
                            maxlength="100" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.address') }}</label>
                        <input type="text" v-model="formData.address"
                            :placeholder="$t('product.form.addressPlaceholder')" maxlength="200" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.email') }}</label>
                        <input type="text" v-model="formData.email" :placeholder="$t('product.form.emailPlaceholder')"
                            maxlength="100" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.whatsapp') }}</label>
                        <input type="text" v-model="formData.whatsapp"
                            :placeholder="$t('product.form.whatsappPlaceholder')" maxlength="30" />
                    </div>
                    <div class="form-group">
                        <label>{{ $t('product.form.remark') }}</label>
                        <textarea v-model="formData.remark" style="height: 120px;"
                            :placeholder="$t('product.form.remarkPlaceholder')" maxlength="500"></textarea>
                    </div>
                    <TurnstileWidget ref="turnstileRef" :sitekey="TURNSTILE_SITE_KEY" @verify="onTurnstileSuccess"
                        @expire="turnstileToken = ''" @error="turnstileToken = ''" />
                </div>
            </div>

            <div class="cart-footer">
                <template v-if="cartState.step === 1">
                    <h3>{{ totalPrice }}</h3>
                    <div class="cart-total">{{ $t('product.cart.totalItems', { count: totalItems }) }}</div>
                    <button class="btn-submit" :disabled="cartState.list.length === 0" @click="cartState.step = 2">
                        {{ $t('product.cart.nextStep') }}
                    </button>
                </template>
                <template v-if="cartState.step === 2">
                    <button class="btn-submit" @click="submitApplication">
                        {{ $t('product.cart.submit') }}
                    </button>
                </template>
            </div>
        </div>

        <transition name="fade">
            <div class="global-loading" v-if="isSubmitting">
                <div class="spinner"></div>
                <p>Processing...</p>
            </div>
        </transition>

        <transition name="fade">
            <div class="modal-overlay" v-if="isMessageModalOpen" @click="closeMessageModal">
                <div class="modal-content message-modal" @click.stop>
                    <div class="message-icon" :class="messageType">
                        <svg v-if="messageType === 'success'" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        <svg v-if="messageType === 'warning'" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                    </div>
                    <h3 class="message-title">{{ messageTitle }}</h3>
                    <p class="message-text">{{ messageText }}</p>
                    <button class="btn-primary btn-block" @click="closeMessageModal">
                        {{ $t('product.alert.confirm') || 'OK' }}
                    </button>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { Order } from '@/api/product'
import TurnstileWidget from '@/components/TurnstileWidget.vue'
import { cartState } from '@/utils/cart'

const { t, locale } = useI18n()
const turnstileRef = ref(null)
const turnstileToken = ref('')
const TURNSTILE_SITE_KEY = '0x4AAAAAAFVXUplqQ584Lc_l'
const isSubmitting = ref(false)
const lastSubmitTime = ref(0)
const submitCooldown = 5000
const honeypot = ref('')
const isMessageModalOpen = ref(false)
const messageType = ref('success')
const messageTitle = ref('')
const messageText = ref('')

const formData = reactive({
    name: '',
    address: '',
    area: '',
    whatsapp: '',
    email: '',
    remark: '',
    zip_code: '',
    order_type: 0,
})

const totalItems = computed(() => cartState.list.reduce((sum, item) => sum + item.quantity, 0))
const totalPrice = computed(() => {
    const total = cartState.list.reduce((sum, item) => sum + Number(item.price || 0) * item.quantity, 0)
    return locale.value === 'zh' ? `￥${(total * 6.8).toFixed(2)}` : `$${total.toFixed(2)}`
})

const iconOf = (item) => {
    const icon = item.icon || ''
    if (icon.startsWith('http')) return icon
    return `https://qiniu-au.wifigsmalarm.com/images/${icon}.png`
}

const showMessage = (type, title, text) => {
    messageType.value = type
    messageTitle.value = title
    messageText.value = text
    isMessageModalOpen.value = true
}

const closeMessageModal = () => {
    isMessageModalOpen.value = false
}

const onTurnstileSuccess = (token) => {
    turnstileToken.value = token
}

const resetSecurityState = () => {
    turnstileToken.value = ''
    honeypot.value = ''
    if (turnstileRef.value && typeof turnstileRef.value.reset === 'function') {
        turnstileRef.value.reset()
    }
}

const increaseQty = (index) => {
    cartState.list[index].quantity++
}

const removeFromCart = (index) => {
    cartState.list.splice(index, 1)
    if (cartState.list.length === 0) cartState.step = 1
}

const decreaseQty = (index) => {
    if (cartState.list[index].quantity > 1) cartState.list[index].quantity--
    else removeFromCart(index)
}

const SQLI_REGEX = /('|"|;|--|\/\*|\*\/|union\s+select|select\s+.*from|insert\s+into|delete\s+from|drop\s+table|update\s+.*set|<script[\s\S]*?>)/i
const sanitizeInput = (str) => (typeof str === 'string' ? str.trim() : '')
const containsDangerousPayload = (obj) => Object.values(obj).some((val) => typeof val === 'string' && SQLI_REGEX.test(val))

const submitApplication = async () => {
    if (isSubmitting.value) return
    if (honeypot.value) {
        showMessage('error', t('product.alert.error'), t('product.alert.invalidRequest'))
        resetSecurityState()
        return
    }
    const now = Date.now()
    if (now - lastSubmitTime.value < submitCooldown) {
        const waitSeconds = Math.ceil((submitCooldown - (now - lastSubmitTime.value)) / 1000)
        showMessage('warning', t('product.alert.notice'), t('product.alert.rateLimit', { seconds: waitSeconds }))
        return
    }
    const cleanName = sanitizeInput(formData.name)
    const cleanArea = sanitizeInput(formData.area)
    const cleanEmail = sanitizeInput(formData.email)
    const cleanWhatsapp = sanitizeInput(formData.whatsapp)
    const cleanZip = sanitizeInput(formData.zip_code)
    const cleanAddress = sanitizeInput(formData.address)
    const cleanRemark = sanitizeInput(formData.remark)
    if (!cleanName || !cleanArea) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.fillRequired'))
        return
    }
    if (cleanEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.invalidEmail'))
        return
    }
    if (cleanWhatsapp && !/^[+0-9\s-]{6,20}$/.test(cleanWhatsapp)) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.invalidPhone'))
        return
    }
    const sanitizedForm = {
        name: cleanName.slice(0, 50),
        area: cleanArea.slice(0, 100),
        address: cleanAddress.slice(0, 200),
        email: cleanEmail.slice(0, 100),
        whatsapp: cleanWhatsapp.slice(0, 30),
        zip_code: cleanZip.slice(0, 20),
        remark: cleanRemark.slice(0, 500),
        order_type: Number(formData.order_type) || 0
    }
    if (containsDangerousPayload(sanitizedForm)) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.dangerousContent'))
        return
    }
    const sanitizedList = cartState.list.map((item) => ({
        id: Number(item.id),
        quantity: Math.max(1, parseInt(item.quantity) || 1),
        price: Number(item.price) || 0
    })).filter((item) => !isNaN(item.id) && item.id > 0)
    if (sanitizedList.length === 0) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.cartEmpty'))
        return
    }
    if (!turnstileToken.value) {
        showMessage('warning', t('product.alert.notice'), t('product.alert.securityCheck'))
        return
    }
    const finalData = {
        turnstile_token: turnstileToken.value,
        honeypot: honeypot.value,
        list: sanitizedList,
        ...sanitizedForm
    }
    isSubmitting.value = true
    lastSubmitTime.value = now
    try {
        await Order(finalData)
        showMessage('success', t('product.alert.success'), t('product.alert.submitSuccess'))
        cartState.list.splice(0, cartState.list.length)
        Object.keys(formData).forEach((key) => { formData[key] = '' })
        resetSecurityState()
        cartState.step = 1
        cartState.open = false
    } catch (error) {
        resetSecurityState()
        showMessage('warning', t('product.alert.notice'), error.message || t('product.alert.submitFailed'))
    } finally {
        isSubmitting.value = false
    }
}
</script>

<style scoped>
.floating-cart {
    position: fixed;
    right: 40px;
    bottom: 80px;
    width: 60px;
    height: 60px;
    background-color: #ffffff;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.4);
    z-index: 99;
    transition: transform 0.2s ease;
}

.floating-cart:hover {
    transform: scale(1.05);
}

.floating-cart.is-bump {
    animation: cart-bump 0.42s ease;
}

.floating-cart.is-bump::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid rgba(0, 188, 212, 0.7);
    animation: cart-ring 0.45s ease-out;
}

.cart-icon {
    font-size: 1.5rem;
}

.cart-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background-color: #ff4d4f;
    color: white;
    font-size: 0.75rem;
    font-weight: bold;
    min-width: 20px;
    height: 20px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 5px;
    border: 2px solid #fff;
}

.cart-overlay,
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100;
}

.modal-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    background: rgba(0, 0, 0, 0.6);
}

.cart-drawer {
    position: fixed;
    top: 0;
    right: -400px;
    width: 400px;
    height: 100vh;
    background: #fff;
    z-index: 101;
    display: flex;
    flex-direction: column;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.1);
    transition: right 0.3s ease;
}

.cart-drawer.open {
    right: 0;
}

.cart-header {
    padding: 20px;
    border-bottom: 1px solid #eaeaea;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.header-left h2 {
    margin: 0;
    font-size: 1.2rem;
}

.back-btn,
.close-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
}

.back-btn {
    font-size: 1.2rem;
}

.close-btn {
    font-size: 1.8rem;
    color: #999;
}

.cart-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #f9f9f9;
}

.empty-cart {
    text-align: center;
    color: #999;
    margin-top: 50px;
}

.cart-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.cart-item {
    background: #fff;
    padding: 12px;
    border-radius: 16px;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    border: 1px solid #eef3f5;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.05);
    text-align: left;
}

.item-img {
    width: 76px;
    height: 76px;
    border-radius: 12px;
    background: #f4fbfc;
    overflow: hidden;
    flex-shrink: 0;
}

.item-img img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
}

.item-info {
    flex: 1;
    min-width: 0;
}

.item-head {
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.item-info h4 {
    flex: 1;
    min-width: 0;
    margin: 2px 0 0;
    font-size: 0.95rem;
    line-height: 1.35;
    font-weight: 600;
    color: #1f2933;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.item-meta {
    margin: 4px 0 0;
    font-size: 0.75rem;
    line-height: 1.4;
    color: #8a94a6;
}

.item-foot {
    margin-top: 10px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
}

.item-price {
    color: #0097a7;
    font-size: 0.95rem;
    font-weight: 700;
    letter-spacing: 0.01em;
}

.qty-control {
    display: flex;
    align-items: center;
    border: 1px solid #e4ebef;
    border-radius: 999px;
    background: #fff;
    overflow: hidden;
    flex-shrink: 0;
}

.qty-control button {
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    color: #334155;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
}

.qty-control button:hover {
    background: #f0fbfd;
    color: #0097a7;
}

.qty-control input {
    width: 28px;
    height: 30px;
    border: none;
    text-align: center;
    outline: none;
    font-weight: 600;
    color: #1f2933;
    background: transparent;
    -moz-appearance: textfield;
    appearance: textfield;
}

.qty-control input::-webkit-outer-spin-button,
.qty-control input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.remove-btn {
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 8px;
    background: #fff5f5;
    color: #ef4444;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: pointer;
}

.remove-btn svg {
    width: 16px;
    height: 16px;
}

.remove-btn:hover {
    background: #ffe4e6;
}

.checkout-form,
.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.checkout-form {
    gap: 20px;
}

.honeypot {
    opacity: 0;
    position: absolute;
    top: -9999px;
    left: -9999px;
    height: 0;
    width: 0;
    pointer-events: none;
}

.form-group label span {
    color: #ff4d4f;
}

.form-group input,
.form-group textarea {
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    outline: none;
}

.cart-footer {
    padding: 20px;
    border-top: 1px solid #eaeaea;
    background: #fff;
}

.cart-total {
    margin-bottom: 15px;
    color: #666;
    font-weight: bold;
}

.btn-submit,
.btn-primary {
    width: 100%;
    padding: 12px 0;
    background: #00bcd4;
    color: #fff;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.btn-submit:disabled {
    background: #ccc;
    cursor: not-allowed;
}

.global-loading {
    position: fixed;
    inset: 0;
    background: rgba(255, 255, 255, 0.75);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 10001;
    color: #00bcd4;
}

.spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e0e0e0;
    border-top-color: #00bcd4;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 15px;
}

.message-modal {
    max-width: 360px;
    text-align: center;
    padding: 40px 30px;
    background: #fff;
    border-radius: 12px;
}

.message-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
}

.message-icon svg {
    width: 32px;
    height: 32px;
}

.message-icon.success {
    background: #d1fae5;
    color: #10b981;
}

.message-icon.warning {
    background: #fef3c7;
    color: #f59e0b;
}

.message-title {
    margin: 0 0 10px;
}

.message-text {
    color: #6b7280;
    margin: 0 0 30px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@keyframes cart-bump {
    0% { transform: scale(1); }
    35% { transform: scale(1.18); }
    70% { transform: scale(0.96); }
    100% { transform: scale(1); }
}

@keyframes cart-ring {
    from { transform: scale(0.85); opacity: 0.8; }
    to { transform: scale(1.45); opacity: 0; }
}

@media (max-width: 768px) {
    .cart-drawer {
        width: 100%;
        right: -100%;
    }
}
</style>

<style>
.cart-fly-token {
    position: fixed;
    z-index: 10002;
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: #fff;
    box-shadow: 0 16px 36px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(0, 188, 212, 0.2);
    overflow: hidden;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: center;
}

.cart-fly-token img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #f6fbfc;
}

.cart-fly-token.is-plain {
    background: linear-gradient(160deg, #4dd0e1, #0097a7);
}

.cart-fly-plus {
    position: absolute;
    top: 4px;
    right: 4px;
    padding: 0 5px;
    border-radius: 999px;
    background: #00bcd4;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 16px;
}
</style>
